import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Key, Copy, RefreshCw, Eye, EyeOff, Shield, Code, CheckCircle2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import api from '@/lib/api';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

const TEMApiTab = () => {
  const { t } = useTranslation();
  const [credentials, setCredentials] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showSecret, setShowSecret] = useState(false);
  const [newSecret, setNewSecret] = useState(null);

  useEffect(() => {
    fetchCredentials();
  }, []);

  const fetchCredentials = async () => {
    try {
      const response = await api.get('/api/seller/tem-api/credentials');
      setCredentials(response.data);
    } catch (error) {
      console.error('Failed to fetch credentials:', error);
    } finally {
      setLoading(false);
    }
  };

  const generateKeys = async () => {
    try {
      const response = await api.post('/api/seller/tem-api/generate-keys');
      setCredentials({
        has_credentials: true,
        api_key: response.data.credentials.api_key,
        api_secret_masked: response.data.credentials.api_secret,
        is_active: true
      });
      setNewSecret(response.data.credentials.api_secret);
      toast.success('API anahtarları oluşturuldu!');
    } catch (error) {
      toast.error('API anahtarları oluşturulamadı');
    }
  };

  const regenerateSecret = async () => {
    try {
      const response = await api.post('/api/seller/tem-api/regenerate-secret');
      setNewSecret(response.data.credentials.api_secret);
      toast.success('API secret yenilendi!');
    } catch (error) {
      toast.error('API secret yenilenemedi');
    }
  };

  const revokeKeys = async () => {
    if (!window.confirm('API anahtarlarını iptal etmek istediğinize emin misiniz?')) return;
    
    try {
      await api.post('/api/seller/tem-api/revoke');
      setCredentials(prev => ({ ...prev, is_active: false }));
      toast.success('API anahtarları iptal edildi');
    } catch (error) {
      toast.error('API anahtarları iptal edilemedi');
    }
  };

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} kopyalandı!`);
  };

  const codeExamples = {
    curl: `# Ürünleri Listele
curl -X GET "${API_BASE}/api/tem-api/v1/products" \\
  -H "X-TEM-API-Key: YOUR_API_KEY" \\
  -H "X-TEM-API-Secret: YOUR_API_SECRET"

# Yeni Ürün Ekle
curl -X POST "${API_BASE}/api/tem-api/v1/products" \\
  -H "X-TEM-API-Key: YOUR_API_KEY" \\
  -H "X-TEM-API-Secret: YOUR_API_SECRET" \\
  -H "Content-Type: application/json" \\
  -d '{
    "name": "Örnek Ürün",
    "price": 199.99,
    "stock": 100,
    "category": "Elektronik"
  }'`,
    python: `import requests

API_KEY = "YOUR_API_KEY"
API_SECRET = "YOUR_API_SECRET"
BASE_URL = "${API_BASE}/api/tem-api/v1"

headers = {
    "X-TEM-API-Key": API_KEY,
    "X-TEM-API-Secret": API_SECRET,
    "Content-Type": "application/json"
}

# Ürünleri Listele
response = requests.get(f"{BASE_URL}/products", headers=headers)
products = response.json()

# Yeni Ürün Ekle
new_product = {
    "name": "Örnek Ürün",
    "price": 199.99,
    "stock": 100,
    "category": "Elektronik"
}
response = requests.post(f"{BASE_URL}/products", json=new_product, headers=headers)

# Stok Güncelle
response = requests.put(f"{BASE_URL}/products/{product_id}/stock", 
    json={"stock": 50}, headers=headers)`,
    javascript: `const API_KEY = 'YOUR_API_KEY';
const API_SECRET = 'YOUR_API_SECRET';
const BASE_URL = '${API_BASE}/api/tem-api/v1';

const headers = {
  'X-TEM-API-Key': API_KEY,
  'X-TEM-API-Secret': API_SECRET,
  'Content-Type': 'application/json'
};

// Ürünleri Listele
const listProducts = async () => {
  const response = await fetch(\`\${BASE_URL}/products\`, { headers });
  return response.json();
};

// Yeni Ürün Ekle
const createProduct = async (product) => {
  const response = await fetch(\`\${BASE_URL}/products\`, {
    method: 'POST',
    headers,
    body: JSON.stringify(product)
  });
  return response.json();
};

// Toplu Stok Güncelle
const batchUpdateStock = async (items) => {
  const response = await fetch(\`\${BASE_URL}/products/stock/batch\`, {
    method: 'PUT',
    headers,
    body: JSON.stringify({ items })
  });
  return response.json();
};`
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-8 text-center">
          <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full mx-auto"></div>
          <p className="mt-4 text-muted-foreground">Yükleniyor...</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold flex items-center gap-2">
          <Code className="w-6 h-6 text-primary" />
          TEM API Entegrasyonu
        </h2>
        <p className="text-muted-foreground mt-1">
          Kendi sistemlerinizden Turkish E-Market'e ürün ekleyin ve yönetin
        </p>
      </div>

      {/* API Credentials Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Key className="w-5 h-5" />
            API Anahtarları
          </CardTitle>
          <CardDescription>
            API Key ve Secret ile ürünlerinizi programatik olarak yönetin
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {!credentials?.has_credentials ? (
            <div className="text-center py-8">
              <Shield className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground mb-4">
                Henüz API anahtarı oluşturmadınız
              </p>
              <Button onClick={generateKeys} data-testid="generate-api-keys-btn">
                <Key className="w-4 h-4 mr-2" />
                API Anahtarı Oluştur
              </Button>
            </div>
          ) : (
            <>
              {newSecret && (
                <Alert className="bg-green-50 border-green-200">
                  <CheckCircle2 className="w-4 h-4 text-green-600" />
                  <AlertDescription className="text-green-800">
                    <strong>Önemli:</strong> API Secret'ı güvenli bir yere kaydedin. Bu değer tekrar gösterilmeyecek!
                    <div className="mt-2 p-2 bg-green-100 rounded font-mono text-sm break-all">
                      {newSecret}
                    </div>
                    <Button 
                      size="sm" 
                      variant="outline" 
                      className="mt-2"
                      onClick={() => copyToClipboard(newSecret, 'API Secret')}
                    >
                      <Copy className="w-3 h-3 mr-1" /> Kopyala
                    </Button>
                  </AlertDescription>
                </Alert>
              )}

              <div className="grid gap-4">
                {/* API Key */}
                <div>
                  <label className="text-sm font-medium text-muted-foreground">API Key</label>
                  <div className="flex gap-2 mt-1">
                    <Input 
                      value={credentials.api_key} 
                      readOnly 
                      className="font-mono text-sm"
                      data-testid="api-key-input"
                    />
                    <Button 
                      variant="outline" 
                      size="icon"
                      onClick={() => copyToClipboard(credentials.api_key, 'API Key')}
                    >
                      <Copy className="w-4 h-4" />
                    </Button>
                  </div>
                </div>

                {/* API Secret (Masked) */}
                <div>
                  <label className="text-sm font-medium text-muted-foreground">API Secret</label>
                  <div className="flex gap-2 mt-1">
                    <Input 
                      value={showSecret && newSecret ? newSecret : credentials.api_secret_masked} 
                      readOnly 
                      type={showSecret ? "text" : "password"}
                      className="font-mono text-sm"
                      data-testid="api-secret-input"
                    />
                    <Button 
                      variant="outline" 
                      size="icon"
                      onClick={() => setShowSecret(!showSecret)}
                    >
                      {showSecret ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    Güvenlik nedeniyle secret maskelenmiştir. Yeni secret almak için yenileyin.
                  </p>
                </div>

                {/* Status & Actions */}
                <div className="flex items-center justify-between pt-4 border-t">
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-muted-foreground">Durum:</span>
                    {credentials.is_active ? (
                      <Badge className="bg-green-100 text-green-800">Aktif</Badge>
                    ) : (
                      <Badge variant="secondary">İptal Edildi</Badge>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={regenerateSecret}>
                      <RefreshCw className="w-4 h-4 mr-1" />
                      Secret Yenile
                    </Button>
                    <Button variant="destructive" size="sm" onClick={revokeKeys}>
                      İptal Et
                    </Button>
                  </div>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* API Documentation */}
      <Card>
        <CardHeader>
          <CardTitle>API Kullanım Örnekleri</CardTitle>
          <CardDescription>
            Farklı programlama dillerinde TEM API kullanım örnekleri
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue="curl">
            <TabsList className="mb-4">
              <TabsTrigger value="curl">cURL</TabsTrigger>
              <TabsTrigger value="python">Python</TabsTrigger>
              <TabsTrigger value="javascript">JavaScript</TabsTrigger>
            </TabsList>
            
            {Object.entries(codeExamples).map(([lang, code]) => (
              <TabsContent key={lang} value={lang}>
                <div className="relative">
                  <pre className="bg-slate-900 text-slate-100 p-4 rounded-lg overflow-x-auto text-sm">
                    <code>{code}</code>
                  </pre>
                  <Button
                    size="sm"
                    variant="secondary"
                    className="absolute top-2 right-2"
                    onClick={() => copyToClipboard(code, 'Kod')}
                  >
                    <Copy className="w-3 h-3 mr-1" /> Kopyala
                  </Button>
                </div>
              </TabsContent>
            ))}
          </Tabs>
        </CardContent>
      </Card>

      {/* API Endpoints Reference */}
      <Card>
        <CardHeader>
          <CardTitle>API Endpoint'leri</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="grid gap-3">
              <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
                <div>
                  <Badge className="bg-green-100 text-green-800 mr-2">GET</Badge>
                  <code className="text-sm">/api/tem-api/v1/products</code>
                </div>
                <span className="text-sm text-muted-foreground">Ürünleri listele</span>
              </div>
              <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
                <div>
                  <Badge className="bg-blue-100 text-blue-800 mr-2">POST</Badge>
                  <code className="text-sm">/api/tem-api/v1/products</code>
                </div>
                <span className="text-sm text-muted-foreground">Yeni ürün ekle</span>
              </div>
              <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
                <div>
                  <Badge className="bg-blue-100 text-blue-800 mr-2">POST</Badge>
                  <code className="text-sm">/api/tem-api/v1/products/batch</code>
                </div>
                <span className="text-sm text-muted-foreground">Toplu ürün ekle (max 100)</span>
              </div>
              <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
                <div>
                  <Badge className="bg-yellow-100 text-yellow-800 mr-2">PUT</Badge>
                  <code className="text-sm">/api/tem-api/v1/products/&#123;id&#125;</code>
                </div>
                <span className="text-sm text-muted-foreground">Ürün güncelle</span>
              </div>
              <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
                <div>
                  <Badge className="bg-yellow-100 text-yellow-800 mr-2">PUT</Badge>
                  <code className="text-sm">/api/tem-api/v1/products/&#123;id&#125;/stock</code>
                </div>
                <span className="text-sm text-muted-foreground">Stok güncelle</span>
              </div>
              <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
                <div>
                  <Badge className="bg-yellow-100 text-yellow-800 mr-2">PUT</Badge>
                  <code className="text-sm">/api/tem-api/v1/products/stock/batch</code>
                </div>
                <span className="text-sm text-muted-foreground">Toplu stok güncelle</span>
              </div>
              <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
                <div>
                  <Badge className="bg-red-100 text-red-800 mr-2">DELETE</Badge>
                  <code className="text-sm">/api/tem-api/v1/products/&#123;id&#125;</code>
                </div>
                <span className="text-sm text-muted-foreground">Ürün sil</span>
              </div>
              <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
                <div>
                  <Badge className="bg-green-100 text-green-800 mr-2">GET</Badge>
                  <code className="text-sm">/api/tem-api/v1/categories</code>
                </div>
                <span className="text-sm text-muted-foreground">Kategorileri listele</span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default TEMApiTab;
